<template>
  <div class="">
    <div class="p-4 font-medium text-xs text-left leading-snug space-y-2 overflow-auto max-h-[40vh] bg-charcoal-100 dark:bg-dark-950">
      <div class="p-4 rounded-xl" :class="[ product.remaining - (formData[product._id] ?? 0) <= 0 && (formData[product._id] ?? 0) <= 0 ? 'bg-charcoal-300 text-charcoal-700 dark:bg-dark-600 dark:text-dark-200' : 'bg-white shadow text-light-950 dark:bg-dark-800 dark:text-dark-50' ]" v-for="product in event['@products']">
        <div class="flex items-center mb-0.5">
          <div class="grow flex items-center space-x-2">
            <p class="text-lg font-bold">{{ product.title }}</p>
            
            <TagBase size="sm" v-if="product.remaining - (formData[product._id] ?? 0) <= 10">
              {{ product.remaining - (formData[product._id] ?? 0) <= 0 ? $t('components.Event.products.soldOut') : $t('components.Event.products.left', product.remaining - (formData[product._id] ?? 0)) }}
            </TagBase>
          </div>
          

          <p class="text-lg font-bold">{{ product.amount }}€</p>
        </div>

        <p class="text-sm text-charcoal-600">{{ product.description }}</p>

        <NuxtLink :to="{ query: { ...route?.query, subscribe: 'true' }}" class="mt-4 bg-cover h-12 rounded-full text-whale-950 text-sm font-bold items-center justify-center flex" :style="{ backgroundImage: `url(${holo})` }" v-if="!currentUser?.isClub && product.isClub">
          {{ $t('actions.subscribe') }}
        </NuxtLink>
        <div class="mt-4 flex items-center space-x-2" v-else-if="!(product.remaining - (formData[product._id] ?? 0) <= 0 && (formData[product._id] ?? 0) <= 0)">
          <InputCount
            class="grow"
            v-model="formData[product._id]"
            :max=" (formData[product._id] ?? 0) + (product.isClub ? 1 : product.remaining) - (formData[product._id] ?? 0)"
          />
        </div>
      </div>
    </div>

    <div class="bg-white border-t border-charcoal-200 p-2.5 dark:text-dark-50 dark:dark:bg-dark-800 dark:border-dark-600">
      <div class="flex items-start mb-2 p-3 -mx-2.5 -mt-2.5 bg-royal-200 font-medium text-royal-950 dark:bg-royal-900 dark:text-royal-300" v-if="(entries - 1) > 0">
        {{ $t('components.Event.products.entries', entries - 1) }}
      </div>

      <div class="bg-ruby-100 text-ruby-950 py-2.5 px-3 leading-tight rounded-xl mb-2" v-if="error">
        {{ $t(`errors.${error.error ?? 'error'}`) }}
      </div>

      <div class="flex items-start mb-2 font-medium">
        <div class="grow pl-2 mt-1">
          <ButtonLink @click="() => isDisplayConditions = true">{{ $t('components.Event.products.conditions.title') }} </ButtonLink>
        </div>
        <div class="shrink-0 bg-charcoal-100 dark:bg-dark-600 rounded-full px-3 py-1.5">Total : {{ totalPrice }}€</div>
      </div>

      <div class="flex items-center space-x-2">
        <ButtonBase style-type="round" class="shrink-0" color="white" @click="() => emit('previous')">
          <IconBase name="cute/angle-right" class="rotate-180" :width="20" />
        </ButtonBase>

        <ButtonBase color="base" is-full @click="() => emit('next')" :is-disabled="entries <= 0">
          {{ totalPrice > 0 ? $t('components.Event.products.action') : $t('components.Event.products.actionFree') }}
        </ButtonBase>
      </div>
    </div>

    <PopinBase size="2xs" :is-active="isDisplayConditions" @close="() => isDisplayConditions = false">
      <template #body>
        <div class="p-6">
          <div v-html="$t('components.Event.products.conditions.content')" />

          <div class="bg-royal-600/20 p-4 rounded-lg mt-4">
            <p class="font-bold text-md">{{ $t('components.Event.refund.title') }}</p>

            <ul class="list-disc pl-4 space-y-4 mt-4">
              <li v-for="i in event.refund.steps.length">
                <span class="font-bold">{{ $t(`components.Event.refund.${event.refundPolicy}.step${i}.title`) }}</span><br>
                {{ $t(`components.Event.refund.${event.refundPolicy}.step${i}.content`) }}
              </li>
            </ul>
          </div>
        </div>
      </template>
    </PopinBase>
  </div>
</template>

<script lang="ts" setup>
import UserStore from '@/store/user'
import holo from '@/assets/img/bg/holo.jpg'
import { Err, Event, EventRefundPolicy } from '@/classes'

const props = withDefaults(defineProps<{
  event: Event
  error: Err | null
}>(), {

})

const emit = defineEmits<{
  (e: 'update', value: { amount: number, products: { [key: string]: number } }): void
  (e: 'next'): void
  (e: 'previous'): void
}>()

const userStore = UserStore()
const { currentUser } = storeToRefs(userStore)

const isDisplayConditions = ref(false)

const route = useRoute()

const { event } = toRefs(props)

const formData: Ref<{ [key: string]: number }> = ref({
  ...event.value.products.reduce((acc, p) => {
    return { [p._id]: 0, ...acc }
  }, {} as { [key: string]: number })
})

const totalProducts = computed(() => {
  return Object.entries(formData.value).map(([key, quantity]) => {
    const product = event.value.products.find(p => p._id === key)
    return { ...product, quantity }
  })
})

const totalPrice = computed(() => {
  return totalProducts.value.reduce((acc, product) => {    
    return acc + (product?.amount ?? 0) * product.quantity
  }, 0)
})

const entries = computed(() => {
  return totalProducts.value.reduce((acc, product) => {
    return acc + (product.isEntry ? product.quantity : 0)
  }, 0)
})

watch(formData, () => {
  emit('update', { products: formData.value, amount: totalPrice.value })
}, { deep: true })

</script>